import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Center, Group, Loader, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { SectionTitle } from './TitleSection';
import { InlineNotice } from '../../components/InlineNotice';
import { BadgeBlock } from '../../components/BadgeBlock';
import { api, bearer } from '../../api/client';
import { logEvent } from '../../lib/telemetry';
import { describeSso, formatSsoDate, type SsoPhase, type SsoStatus } from './ssoStatus';

const pageData = {
  title: 'Single sign-on',
  subtitle: 'Let your users sign in with your organisation’s identity provider.',
};

const STATUS_FAILED = 'cloudhouse.enterprise.sso.status_failed';
const SETUP_STARTED = 'cloudhouse.enterprise.sso.setup_started';
const SETUP_FAILED = 'cloudhouse.enterprise.sso.setup_failed';
const REMOVED = 'cloudhouse.enterprise.sso.removed';
const REMOVE_FAILED = 'cloudhouse.enterprise.sso.remove_failed';

const NO_PERMISSION = "You don't have permission to manage single sign-on. Ask an organisation admin with that permission, or contact Cloudhouse support.";
const WELCOME_BACK = 'Welcome back. Your single sign-on settings are shown below.';

const ACTION_ERRORS: Record<string, string> = {
  forbidden: NO_PERMISSION,
  not_entitled: 'Single sign-on comes with a paid subscription and is not available during trials.',
  invalid_request: 'That request was not valid. Refresh and try again.',
  organisation_deleted: 'Your organisation has been deleted.',
  no_connection: 'There is no single sign-on connection to remove.',
  workos_failed: "The identity service couldn't complete the action — try again shortly.",
  audit_unavailable: "The action couldn't be recorded to the audit log — try again shortly.",
};

// The rate limiter answers with an empty body, so the status is the only signal.
function actionErrorText(status: number | undefined, code: string | undefined): string {
  if (status === 429) return 'Too many attempts. Wait a minute and try again.';
  return (code && ACTION_ERRORS[code]) || 'The action failed — please try again in a moment.';
}

type PortalIntent = 'sso' | 'domain_verification';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; status: SsoStatus }
  | { kind: 'forbidden' }
  | { kind: 'error' };

export function ProfileSso() {
  const auth = useAuth();
  const orgId = auth.organizationId ?? null;
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // WorkOS returns the admin here after the portal (success_url); greet them so the reload reads as intended.
  const [actionNotice, setActionNotice] = useState<string | null>(searchParams.get('from') === 'portal' ? WELCOME_BACK : null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    const token = await auth.getAccessToken();
    if (!token) { setState({ kind: 'error' }); return; }
    const { data, error, response } = await api.GET('/organisations/sso', { headers: bearer(token), signal });
    if (error || !data) {
      const status = response?.status ?? 0;
      logEvent(STATUS_FAILED, 'SSO status read failed', { org_id: orgId, status }, 'WARN');
      setState(status === 403 ? { kind: 'forbidden' } : { kind: 'error' });
      return;
    }
    // Fail closed on the flag: a missing value renders the paid-subscription state, never the setup button.
    setState({ kind: 'ready', status: { entitled: data.entitled === true, connection: data.connection ?? null, domains: data.domains ?? [] } });
  }, [auth, orgId]);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        await load(ctrl.signal);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setState({ kind: 'error' });
      }
    })();
    return () => ctrl.abort();
  }, [load]);

  // The portal link is a five-minute single-use secret: request it and go, never display it.
  async function openPortal(intent: PortalIntent) {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    setActionNotice(null);
    try {
      const token = await auth.getAccessToken();
      if (!token) { setActionError(actionErrorText(undefined, undefined)); return; }
      const { data, error, response } = await api.POST('/organisations/sso/portal-link', {
        body: { intent },
        headers: bearer(token),
      });
      if (error || !data?.link) {
        const status = response?.status ?? 0;
        logEvent(SETUP_FAILED, 'SSO portal link request failed', { org_id: orgId, intent, status }, 'WARN');
        setActionError(actionErrorText(status, error?.error));
        return;
      }
      logEvent(SETUP_STARTED, 'Org admin opened the WorkOS Admin Portal', { org_id: orgId, intent });
      globalThis.location.assign(data.link);
    } catch {
      logEvent(SETUP_FAILED, 'SSO portal link request failed', { org_id: orgId, intent, status: 0 }, 'WARN');
      setActionError(actionErrorText(undefined, undefined));
    } finally {
      setBusy(false);
    }
  }

  // The API deletes before it audits, so a failure response can still mean the connection is gone:
  // status is reloaded after every answer, never inferred from the last render.
  async function removeConnection() {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    setActionNotice(null);
    let answered = false;
    try {
      const token = await auth.getAccessToken();
      if (!token) { setActionError(actionErrorText(undefined, undefined)); return; }
      const { error, response } = await api.DELETE('/organisations/sso/connection', { headers: bearer(token) });
      answered = true;
      if (error) {
        const status = response?.status ?? 0;
        logEvent(REMOVE_FAILED, 'SSO connection removal failed', { org_id: orgId, status }, 'WARN');
        setConfirmingRemove(false);
        setActionError(actionErrorText(status, error.error));
        return;
      }
      logEvent(REMOVED, 'Org admin removed the SSO connection', { org_id: orgId });
      setConfirmingRemove(false);
      setActionNotice('Single sign-on removed. Users now sign in with a password.');
    } catch {
      logEvent(REMOVE_FAILED, 'SSO connection removal failed', { org_id: orgId, status: 0 }, 'WARN');
      setActionError(actionErrorText(undefined, undefined));
    } finally {
      setBusy(false);
      if (answered) await load();
    }
  }

  return (
    <Stack flex={1} style={{ overflowY: 'auto' }} gap="lg" px={32} py={24}>
      <SectionTitle {...pageData} />

      {state.kind === 'loading' && <Center flex={1}><Loader /></Center>}
      {state.kind === 'forbidden' && <PageAlert message={NO_PERMISSION} />}
      {state.kind === 'error' && <PageAlert message="We couldn't read your single sign-on settings. Please refresh in a moment." />}
      {state.kind === 'ready' && (
        <StatusCard
          status={state.status}
          busy={busy}
          onSetup={() => void openPortal('sso')}
          onVerifyDomain={() => void openPortal('domain_verification')}
          onRemove={() => { setConfirmText(''); setActionError(null); setConfirmingRemove(true); }}
        />
      )}

      {actionError && !confirmingRemove && <InlineNotice tone="error" message={actionError} onClose={() => setActionError(null)} />}
      {actionNotice && <InlineNotice tone="success" message={actionNotice} onClose={() => setActionNotice(null)} />}

      <Modal opened={confirmingRemove}
        onClose={() => { if (!busy) setConfirmingRemove(false); }}
        title="Remove single sign-on?" centered
        styles={{
          header: { position: 'relative' },
          title: { flex: 1, textAlign: 'center' },
          close: { position: 'absolute', right: 'var(--mantine-spacing-md)' },
        }}>
        <Stack gap="md">
          <Text fz="sm" fw={600} c="var(--status-danger)">This cannot be undone.</Text>
          <Text fz="sm">
            Deletes the connection to your identity provider. Your users will sign in with a password instead.
            To confirm, type <Text component="span" fz="sm" fw={600} ff="monospace">REMOVE</Text> below.
          </Text>
          <TextInput
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
            placeholder="Type REMOVE"
            aria-label="Type REMOVE to confirm"
            disabled={busy}
          />
          {actionError && <InlineNotice tone="error" message={actionError} onClose={() => setActionError(null)} />}
          <Group justify="flex-end" gap={10}>
            <Button variant="default" disabled={busy} onClick={() => setConfirmingRemove(false)}>Cancel</Button>
            <Button color="red" loading={busy} disabled={confirmText.trim() !== 'REMOVE'}
              onClick={() => void removeConnection()}>
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function PageAlert({ message }: Readonly<{ message: string }>) {
  return (
    <Alert color="var(--status-danger)" variant="light" p="xl" ta="center"
      style={{ borderColor: 'var(--status-danger-background)', '--alert-bg': 'var(--status-danger-background)' }}>
      <Text c="var(--status-danger)" size="sm">{message}</Text>
    </Alert>
  );
}

const STATUS_COLORS: Record<SsoPhase, string> = {
  active: 'var(--status-success)',
  draft: 'var(--status-warning)',
  'not-entitled': 'var(--text-muted)',
  'not-set-up': 'var(--text-muted)',
};

function domainBadge(state: string) {
  return state === 'verified'
    ? <BadgeBlock fz="xs" bdrs="xl" color="var(--status-success)" display="flex">Verified</BadgeBlock>
    : <BadgeBlock fz="xs" bdrs="xl" color="var(--status-warning)" display="flex">Pending verification</BadgeBlock>;
}

function StatusCard({ status, busy, onSetup, onVerifyDomain, onRemove }: Readonly<{
  status: SsoStatus;
  busy: boolean;
  onSetup: () => void;
  onVerifyDomain: () => void;
  onRemove: () => void;
}>) {
  const view = describeSso(status);
  const statusColor = STATUS_COLORS[view.phase];
  const primaryLabel = view.primaryAction === 'setup' ? 'Set up single sign-on' : 'Manage single sign-on';

  return (
    <Box data-testid="sso-status" p={20}
      style={{ border: '1px solid var(--border-default)', borderRadius: 12, background: 'var(--bg-surface)' }}>
      <Stack gap={14}>
        <Group gap={10}>
          <Box w={10} h={10} bdrs="50%" bg={statusColor} />
          <Text fz="sm" fw={700}>{view.headline}</Text>
        </Group>
        <Text fz="sm" c="var(--text-muted)">{view.detail}</Text>

        <Stack gap={6}>
          {view.provider && (
            <Group gap={12}>
              <Text fz="xs" fw={700} c="var(--text-subtle)" tt="uppercase" w={80}>Provider</Text>
              <Text fz="sm">{view.provider}</Text>
            </Group>
          )}
          {status.domains.map((d) => (
            <Group gap={12} key={d.domain}>
              <Text fz="xs" fw={700} c="var(--text-subtle)" tt="uppercase" w={80}>Domain</Text>
              <Text fz="sm">{d.domain}</Text>
              {domainBadge(d.state)}
            </Group>
          ))}
          {status.connection && (
            <Group gap={12}>
              <Text fz="xs" fw={700} c="var(--text-subtle)" tt="uppercase" w={80}>Updated</Text>
              <Text fz="sm">{formatSsoDate(status.connection.updatedAt)}</Text>
            </Group>
          )}
        </Stack>

        <Group gap={10}>
          {view.primaryAction !== 'none' && (
            <Button loading={busy} onClick={onSetup}>{primaryLabel}</Button>
          )}
          {view.needsDomainVerification && (
            <Button variant="default" disabled={busy} onClick={onVerifyDomain}>Verify domain</Button>
          )}
          {view.canRemove && (
            <Button color="red" variant="outline" disabled={busy} onClick={onRemove}>Remove single sign-on</Button>
          )}
        </Group>
      </Stack>
    </Box>
  );
}
